/*
  Pulses for the trace artwork (snippets/traces-gutters.liquid).

  A pulse is a short bead that rides a trace from one end to the other. IsikPCB draws it as a dash
  moved along the path with stroke-dashoffset, but animating that property repaints the whole page
  on the main thread every frame. Here every straight segment of the trace gets its own window
  instead, clipped to the segment and overlapping its neighbors by the miter at each bend, and the
  same dash slides through all of them with a transform. Transform and opacity animations run on
  the compositor, so nothing runs per frame on the main thread; the motion itself is the CSS
  keyframes in component-traces.css.

  Built once per <trace-art> element, the first time its media query (the "Minimum window width"
  setting) matches, so narrower screens never build anything. Lengths come from each trace's own
  geometry. Pulsing traces must be polylines (M, L, H and V commands); any other trace keeps no
  pulse. The animations start together once everything is built, so pulses, flashes and fades
  stay in step.
*/

if (!customElements.get('trace-art')) {
  customElements.define(
    'trace-art',
    class TraceArt extends HTMLElement {
      static SPEED = 180; // viewBox units per second
      static PULSE = 70; // length of the bead, in viewBox units
      static TRAVEL = 0.42; // fraction of each cycle spent moving; the rest is silence

      connectedCallback() {
        if (this.hasAttribute('data-pulses')) return;
        // Kept on the element so the listener lives as long as the element does.
        this.mediaQuery = this.dataset.media ? window.matchMedia(this.dataset.media) : null;
        if (!this.mediaQuery || this.mediaQuery.matches) {
          this.build();
        } else {
          this.mediaQuery.addEventListener('change', () => this.build(), { once: true });
        }
      }

      build() {
        if (this.hasAttribute('data-pulses')) return;
        const { SPEED, PULSE, TRAVEL } = TraceArt;

        this.querySelectorAll('path[data-sig]').forEach((path) => {
          const segments = TraceArt.segments(path.getAttribute('d'));
          if (!segments) return;
          const length = segments.reduce((total, segment) => total + segment.length, 0);
          const cycle = length / SPEED / TRAVEL;
          const delay = Number(path.dataset.delay) || 0;
          const svg = path.ownerSVGElement;
          const key = path.dataset.sig;

          svg.querySelectorAll(`.trace-signal[data-for="${key}"]`).forEach((pulse) => {
            const width = Number(pulse.getAttribute('stroke-width')) || 1;
            pulse.style.setProperty('--cycle', `${cycle.toFixed(2)}s`);
            pulse.style.setProperty('--delay', `${delay}s`);
            // The tail of the dash travels from before the start to the end of the trace.
            pulse.style.setProperty('--from', `${(-0.2 * length).toFixed(2)}px`);
            pulse.style.setProperty('--to', `${length.toFixed(2)}px`);

            segments.forEach((segment, index) => {
              const before = index > 0 ? TraceArt.miter(segments[index - 1], segment, width) : 0;
              const after = index < segments.length - 1 ? TraceArt.miter(segment, segments[index + 1], width) : 0;
              const span = before + segment.length + after;
              const frame = TraceArt.create('g', pulse, {
                transform: `translate(${segment.x} ${segment.y}) rotate(${segment.angle})`,
              });
              const clip = TraceArt.create('svg', frame, {
                x: -before,
                y: -width,
                width: span,
                height: 2 * width,
                viewBox: `${-before} ${-width} ${span} ${2 * width}`,
              });
              TraceArt.create('path', clip, { class: 'trace-signal__dash', d: `M${-segment.start} 0 h${PULSE}` });
            });
          });

          // The flash peaks as the pulse reaches the far end. A via at the start of the trace is
          // shifted back by the travel time, so it flashes as the pulse leaves instead.
          svg.querySelectorAll(`.trace-arrive[data-for="${key}"]`).forEach((pad) => {
            const shift = pad.dataset.at === 'start' ? TRAVEL * cycle : 0;
            pad.style.setProperty('--cycle', `${cycle.toFixed(2)}s`);
            pad.style.setProperty('--delay', `${(delay - shift).toFixed(2)}s`);
          });
        });

        this.setAttribute('data-pulses', '');
      }

      // Straight segments of a polyline path, each with its start point, direction and distance
      // from the start of the path. Returns null for anything that isn't a polyline.
      static segments(d) {
        const tokens = (d || '').match(/[a-z]|-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi) || [];
        const points = [];
        let command = '';
        let x = 0;
        let y = 0;
        let i = 0;
        const number = () => Number(tokens[i++]);
        while (i < tokens.length) {
          if (/^[a-z]$/i.test(tokens[i])) command = tokens[i++];
          switch (command) {
            case 'M':
            case 'L':
              x = number();
              y = number();
              break;
            case 'm':
            case 'l':
              x += number();
              y += number();
              break;
            case 'H':
              x = number();
              break;
            case 'h':
              x += number();
              break;
            case 'V':
              y = number();
              break;
            case 'v':
              y += number();
              break;
            default:
              return null;
          }
          if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
          points.push([x, y]);
          // Coordinates after a moveto are linetos.
          if (command === 'M') command = 'L';
          if (command === 'm') command = 'l';
        }

        const segments = [];
        let start = 0;
        for (let p = 1; p < points.length; p++) {
          const [x0, y0] = points[p - 1];
          const [x1, y1] = points[p];
          const length = Math.hypot(x1 - x0, y1 - y0);
          if (length === 0) continue;
          segments.push({ x: x0, y: y0, angle: (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI, start, length });
          start += length;
        }
        return segments.length ? segments : null;
      }

      // How far the windows on either side of a bend reach past it, so that together they draw
      // exactly the mitered corner of a stroke this wide.
      static miter(from, to, width) {
        const turn = (Math.abs(to.angle - from.angle) % 360) * (Math.PI / 180);
        const bend = Math.min(turn, 2 * Math.PI - turn);
        return Math.min((width / 2) * Math.tan(bend / 2), width);
      }

      static create(name, parent, attributes) {
        const element = document.createElementNS('http://www.w3.org/2000/svg', name);
        Object.entries(attributes).forEach(([attribute, value]) => element.setAttribute(attribute, value));
        parent.append(element);
        return element;
      }
    }
  );
}
